import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchUserSavedItineraries, deleteUserItinerary } from '../api/authApi';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../config';
import './MyItinerariesPage.css';

// Utility to check JWT expiry
function isTokenExpired(token) {
    if (!token) return true;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!payload.exp) return true;
      return Date.now() >= payload.exp * 1000;
    } catch  {
      
      return true;
    }
  }
  function parseTravelMinutes(value) {
    if (typeof value === "number") {
      return Math.max(0, Math.round(value));
    }
  
    if (typeof value !== "string") {
      return 0;
    }
  
    const dayMatch = value.match(/(\d+)\s*day/i);
    const hourMatch = value.match(/(\d+)\s*(?:hr|hour)/i);
    const minuteMatch = value.match(/(\d+)\s*min/i);
  
    const days = dayMatch ? Number(dayMatch[1]) : 0;
    const hours = hourMatch ? Number(hourMatch[1]) : 0;
    const minutes = minuteMatch ? Number(minuteMatch[1]) : 0;
  
    return days * 1440 + hours * 60 + minutes;
  }

const RouteThumbnail = ({ tripData }) => {
    return (
      <div className="route-flow">
        <div className="route-step">
          <div className="dot" />
          <span>{tripData.origin}</span>
        </div>
        {tripData.destinations?.map((city, idx) => (
          <React.Fragment key={idx}>
            <div className="route-line" />
            <div className="flight-icon">✈️</div>
            <div className="route-line" />
            <div className="route-step">
              <div className="dot" />
              <span>{city}</span>
              {tripData.daysPerDestination?.[idx] && (
                <div className="days-container">
                  <span className="days-box">{tripData.daysPerDestination[idx]}</span>
                  <span className="days-text">days</span>
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
        <div className="route-line" />
        <div className="flight-icon">✈️</div>
        <div className="route-line" />
        <div className="route-step">
          <div className="dot" />
          <span>{tripData.origin}</span>
        </div>
      </div>
    );
  };
  
const MyItinerariesPage = () => {
  const location = useLocation();
  const { accessToken, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');


  const handleDelete = async (savedId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this saved itinerary?");
    if (!confirmDelete) return;
  
    try {
        await deleteUserItinerary(savedId, accessToken);
      // Remove from state without refetching
      setItineraries((prev) => prev.filter((item) => item.saved_id !== savedId));
    } catch  {
     
      alert('Failed to delete itinerary. Please try again.');
    }
  };
  
  const AttractionCardReadOnly = ({ place, top, height, travelTime }) => (
    <>
      {travelTime > 0 && (
  <>
    <div
      className="travel-line-readonly"
      style={{
        height: `${(travelTime / 60) * 100}px`,
        top: `${top - (travelTime / 60) * 100}px`,
        left: '50%'
      }}
    ></div>
    <div
      className="travel-time-readonly"
      style={{ top: `${top - 30}px`, left: '50%' }}
    >
      <img src="/icons/car-side-svgrepo-com.svg" alt="Car Icon" />
      {travelTime} min
    </div>
  </>
)}
      <div
        className="attraction-card-readonly"
        style={{ top: `${top}px`, height: `${height}px` }}
      >
        <strong>{place.name}</strong>
        <p>{place.address}</p>
        <p>Duration: {place.visit_duration} hrs</p>
      </div>
    </>
  );
  const handleEdit = (itinerary) => {
    navigate(`/itinerary`, {
      state: {
        itineraryData: itinerary.itinerary_data,
        tripData: itinerary.trip_data,
        savedTitle: itinerary.title,
        isEditing: true,
        savedId: itinerary.saved_id, 
      }
    });
  };
  const handleShare = async (savedId) => {
    try {
      const res = await fetch(api.url(`/api/saved-itineraries/${savedId}/share`), {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` }
      });
  
      if (res.ok) {
        const data = await res.json();
        setShareUrl(data.share_url);
        setCopySuccess('');
        setShareModalOpen(true);
      } else {
        alert('Failed to generate share link');
      }
    } catch  {
      
      alert('Error generating share link');
    }
  };
  
  
  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopySuccess('Copied!');
  };
  
  const DayColumnReadOnly = ({ dayData }) => {
    const GRID_HEIGHT = 25;
    
    return (
      <div className="calendar-column day-column">
        <div className="cell day-header">
          <div className="day-number">Day {dayData.day}</div>
          <div className="day-city">{dayData.city}</div>
        </div>
  
        <div className="grid-background">
          {Array.from({ length: 49 }).map((_, i) => (
            <div key={i} className="grid-hour-line" />
          ))}
        </div>
  
        {dayData.places
          .sort((a, b) => a.start_time - b.start_time)
          .map((place, idx) => {
            const top = (place.start_time + 0.5) * 4 * GRID_HEIGHT;
            const height = place.visit_duration * 4 * GRID_HEIGHT;
            const travelTime = parseTravelMinutes(
              place.travel_time_from_previous
            );
            return (
              <AttractionCardReadOnly
                key={idx}
                place={place}
                top={top}
                height={height}
                travelTime={travelTime}
              />
            );
          })}
      </div>
    );
  };
  const CalendarViewReadOnly = ({ itineraryData }) => {
    const scrollRef = useRef(); 
    useEffect(() => {
      const gridHeight = 25; // Each 30-minute block = 25px
      const scrollToIndex = 40; // 10:00 AM = 20 blocks
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollToIndex * gridHeight;
      }
    }, []);
    return (
      
      <div className="calendar-wrapper-readonly" ref={scrollRef}>
        <div className="calendar-grid-readonly">
          <div className="calendar-column time-column">
            <div className="cell time-header"></div>
            {Array.from({ length: 48 }).map((_, i) => {
              const hour = Math.floor(i / 2);
              const minutes = i % 2 === 0 ? "00" : "30";
              return (
                <div key={i} className="cell time-slot">
                  {`${hour.toString().padStart(2, "0")}:${minutes}`}
                </div>
              );
            })}
          </div>
  
          {itineraryData.map((dayData, idx) => (
            <DayColumnReadOnly key={idx} dayData={dayData} />
          ))}
        </div>
      </div>
      
      
    );
  };
      

  useEffect(() => {
    // Redirect immediately if not logged in OR token expired
    if (!isLoggedIn || isTokenExpired(accessToken)) {
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      
  
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await fetchUserSavedItineraries(accessToken);
        setItineraries(data);
      } catch  {
        
        setError("Failed to load your itineraries. Please try again.");
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
}, [accessToken, isLoggedIn, navigate, location.pathname]);
  

  if (loading) {
    return <div>Loading your itineraries...</div>;
  }

  if (error) {
    return <div style={{ color: 'red' }}>{error}</div>;
  }

  return (
    <div className="my-itineraries-page">
       <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>
      <h1>My Itineraries</h1>  
      {itineraries.length === 0 ? (
      <div>You haven't saved any itineraries yet.</div>
    ) : (
      <div className="itinerary-list">
        {itineraries.map((itinerary) => {
          let tripData = {};
          let itineraryData = {};

          try {
            tripData = itinerary.trip_data ?? {};
          } catch  {
            
          }

          try {
            itineraryData = itinerary.itinerary_data ?? {};
          } catch  {
            
          }

          const isExpanded = expandedId === itinerary.saved_id;

          return (
            <div
  key={itinerary.saved_id}
  className={`itinerary-card ${isExpanded ? 'expanded' : ''}`}
  onClick={() => setExpandedId(isExpanded ? null : itinerary.saved_id)}
>
  <button
    className="delete-button"
    onClick={(e) => {
      e.stopPropagation();
      handleDelete(itinerary.saved_id);
    }}
  >
    ✕
  </button>
  <div className="itinerary-actions">
  <div className="itinerary-share-icon" onClick={(e) => {
  e.stopPropagation();
  handleShare(itinerary.saved_id);
}}>
  <img src="/icons/share-svgrepo-com.svg" alt="Share" />
  <span className="tooltip-text">Share</span>
</div>

  <div
    className="itinerary-edit-icon"
    onClick={(e) => {
      e.stopPropagation();
      handleEdit(itinerary);  
    }}
  >
    <img src="/icons/edit-pencil-svgrepo-com.svg" alt="Edit" />
    <span className="tooltip-text">Edit</span>
  </div>
  </div>
  <div className="card-main-content">
  <div className="route-thumbnail">
    <RouteThumbnail tripData={tripData} />
    {itinerary.title && (
      <div className="itinerary-title">
        <strong>{itinerary.title}</strong>
      </div>
    )}
  </div>

  {isExpanded && (
  <div className="day-by-day-details">
    <h4>Day-by-Day Itinerary</h4>
    <div className="calendar-scroll-container">
      <CalendarViewReadOnly itineraryData={itineraryData} />
    </div>
  </div>
)}

</div>
</div>

          )
        })}
      </div>
    )}
    {shareModalOpen && (
  <div className="modal-overlay">
    <div className="share-modal-box">
      <h2 className="modal-title">Share Itinerary</h2>

      <div className="modal-url-section">
        <input className="share-url-input" type="text" value={shareUrl} readOnly />
        <button className="copy-button" onClick={handleCopy}>📋 Copy</button>
        {copySuccess && <span className="copy-success">{copySuccess}</span>}
      </div>

     

      <button className="close-button" onClick={() => setShareModalOpen(false)}>Close</button>
    </div>
  </div>
)}

    </div>
  );
}

export default MyItinerariesPage;
